/**
 * 一次性临时口令生成 — 只服务「重置密码」那一枚由管理员现场交出的新口令。
 *
 * 为什么在**前端**生成：后端 U2（`POST /api/users/{id}/password/reset`）原文是「服务端随机生成→哈希落库→
 * 明文即用即弃」，契约又写着「返回结果不含口令」，本环境还没有短信/邮件下发通道 ⇒ 重置完**谁都不知道口令**，
 * 等于把账号锁死（管理端 2026-10-09 现场：重置后只弹「临时密码：undefined」）。
 * 裁定后的口径：口令由**管理员本人设定或在此生成**，经线下渠道转交本人——口令的知情面＝操作者＋接手人。
 * 后端只接受请求体里那枚 `newPassword`（增量可选面），响应仍不回显（契约行不动）。
 *
 * 本文件零 import、⛔ 不碰网络/存储/console：口令明文只允许活在调用它的那个组件的 ref 里，
 * 落 store／localStorage／日志都算新开一条泄漏通道（红线 8）。
 */

/** 生成长度（后端 `ValidatePasswordStrength` 只要求 ≥8 位且含大写+小写+数字，这里给足） */
export const TEMP_PASSWORD_LENGTH = 12;

// 口令池刻意剔掉易混形：I/O/0/1/l —— 这枚口令要被人肉眼抄写或念读转交，歧义字＝白跑一趟。
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const DIGIT = '23456789';
const SYMBOL = '!@#$%&*+-=?';
const POOL = UPPER + LOWER + DIGIT + SYMBOL;

/**
 * 与后端 `UserManagementModule.ValidatePasswordStrength` 同判据的前端预检：≥8 位且含大写、小写、数字。
 * ⛔ 拿它当安全边界——真闸在后端（前端只是把必然被拒的输入挡在提交前）。
 * ⛔ 顺手加符号要求：后端没这要求，两边判据不一致会让管理员改不了自己的输入。
 */
export function meetsPasswordStrength(password: string): boolean {
  const value = String(password ?? '');
  return value.length >= 8 && /[A-Z]/.test(value) && /[a-z]/.test(value) && /\d/.test(value);
}

/** 取一枚 [0, bound) 的均匀随机下标；⛔ 取模偏差、⛔ 缺随机源时静默回落伪随机（口令弱随机＝可预测） */
function randomIndex(bound: number): number {
  const host = globalThis as { crypto?: Crypto };
  const cryptoObj = host.crypto;
  if (!cryptoObj || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error('当前运行环境不支持安全随机数：拒绝生成可预测口令');
  }
  const limit = Math.floor(0x100000000 / bound) * bound;
  const buf = new Uint32Array(1);
  for (let attempt = 0; attempt < 64; attempt += 1) {
    cryptoObj.getRandomValues(buf);
    const value = buf[0];
    if (value < limit) return value % bound;
  }
  // 连续 64 次落进拒绝区的概率 < 2^-64 ⇒ 真发生说明随机源坏了，宁可报错也不给一枚可疑口令
  throw new Error('随机源异常：连续 64 次拒绝采样未命中，拒绝生成口令');
}

function pickFrom(pool: string): string {
  return pool.charAt(randomIndex(pool.length));
}

/**
 * 生成一枚临时口令：四类字符各保底一枚（保证过后端强度闸），补足长度后整体 Fisher–Yates 洗牌
 * （不洗的话「第 1 位大写、第 2 位小写…」这个形状本身就是可预测信息）。
 */
export function generateTempPassword(length: number = TEMP_PASSWORD_LENGTH): string {
  const size = Math.max(8, Math.min(64, Math.trunc(length)));
  const chars: string[] = [pickFrom(UPPER), pickFrom(LOWER), pickFrom(DIGIT), pickFrom(SYMBOL)];
  while (chars.length < size) chars.push(pickFrom(POOL));
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    const tmp = chars[i];
    chars[i] = chars[j];
    chars[j] = tmp;
  }
  return chars.join('');
}
